import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import backendVerifiedUser from '../../api/backendVerifiedUser';
import { BASE_URL } from '../../config/apiConfig';

// POST that answers with an event stream; calls onEvent(name, data) per event
async function streamPost(path, body, onEvent) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('myToken')}` },
    body: JSON.stringify(body),
  });
  if (res.status === 401) {
    localStorage.clear();
    window.location.href = '/';
    return;
  }
  if (!(res.headers.get('content-type') || '').includes('text/event-stream')) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json?.error?.msg || `Request failed (${res.status})`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const chunk = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let event = 'message';
      let data = '';
      chunk.split('\n').forEach((line) => {
        if (line.startsWith('event: ')) event = line.slice(7);
        else if (line.startsWith('data: ')) data += line.slice(6);
      });
      if (data) {
        try {
          onEvent(event, JSON.parse(data));
        } catch (e) {
          console.error('bad event', e);
        }
      }
    }
  }
}

const upsertTool = (parts, id, patch) => {
  const i = parts.findIndex((p) => p.type === 'tool' && p.id === id);
  if (i === -1) parts.push({ type: 'tool', id, ...patch });
  else parts[i] = { ...parts[i], ...patch };
};

/**
 * Chat state for one AI Assistant conversation (used by the full page and the floating widget).
 * @param {{ onRunEnd?: (conversationId) => void }} options
 */
export default function useAssistantChat({ onRunEnd } = {}) {
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = activeId;
  const loadSeq = useRef(0); // a newer open / new chat wins over a slower earlier load

  const openConversation = useCallback(async (id) => {
    const seq = ++loadSeq.current;
    const res = await backendVerifiedUser.get(`/ai-assistant/conversations/${id}`);
    if (seq !== loadSeq.current) return;
    const data = res.data.success.data;
    setActiveId(data.id);
    setMessages(data.messages);
    setPending(data.pending?.length ? data.pending : null);
  }, []);

  const newChat = useCallback(() => {
    loadSeq.current++;
    setActiveId(null);
    setMessages([]);
    setPending(null);
    inputRef.current?.focus();
  }, []);

  // Apply a stream event to the last (assistant) message
  const updateAssistant = (fn) =>
    setMessages((prev) => {
      const next = [...prev];
      let last = next[next.length - 1];
      if (!last || last.role !== 'assistant') {
        last = { role: 'assistant', parts: [] };
        next.push(last);
      } else {
        last = { ...last, parts: [...last.parts] };
        next[next.length - 1] = last;
      }
      fn(last.parts);
      return next;
    });

  const handleEvent = (event, data) => {
    switch (event) {
      case 'conversation':
        setActiveId(data.id);
        activeRef.current = data.id;
        break;
      case 'text':
        updateAssistant((parts) => {
          const last = parts[parts.length - 1];
          if (last?.type === 'text') parts[parts.length - 1] = { ...last, text: last.text + data.delta };
          else parts.push({ type: 'text', text: data.delta });
        });
        break;
      case 'reset_text':
        // a model turn was discarded (refused / retried) — drop its streamed text
        updateAssistant((parts) => {
          if (parts[parts.length - 1]?.type === 'text') parts.pop();
        });
        break;
      case 'tool_preparing':
        updateAssistant((parts) => upsertTool(parts, data.id, { label: data.label, status: 'preparing' }));
        break;
      case 'tool_start':
        updateAssistant((parts) =>
          upsertTool(parts, data.id, { label: data.label, name: data.name, summary: data.summary, write: data.write, status: 'running' })
        );
        break;
      case 'tool_result':
        updateAssistant((parts) => upsertTool(parts, data.id, { status: data.status, error: data.error }));
        break;
      case 'confirm':
        setPending(data.actions);
        updateAssistant((parts) =>
          data.actions.forEach((a) => upsertTool(parts, a.id, { label: a.label, name: a.name, summary: a.summary, write: true, status: 'pending' }))
        );
        break;
      case 'retract_user':
        setMessages((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i--) {
            if (next[i].role === 'user') {
              next[i] = { ...next[i], retracted: true };
              break;
            }
          }
          return next;
        });
        break;
      case 'error':
        updateAssistant((parts) => parts.push({ type: 'error', msg: data.msg }));
        break;
      default:
        break;
    }
  };

  const run = async (path, body) => {
    setBusy(true);
    try {
      await streamPost(path, body, handleEvent);
    } catch (err) {
      toast.error(err.message);
      updateAssistant((parts) => parts.push({ type: 'error', msg: err.message }));
    } finally {
      setBusy(false);
      onRunEnd?.(activeRef.current);
      inputRef.current?.focus();
    }
  };

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || busy) return;
    loadSeq.current++;
    setInput('');
    if (pending) {
      // typing instead of Approve/Reject rejects the waiting action
      setMessages((prev) =>
        prev.map((m) =>
          m.role === 'assistant'
            ? { ...m, parts: m.parts.map((p) => (p.type === 'tool' && p.status === 'pending' ? { ...p, status: 'declined' } : p)) }
            : m
        )
      );
      setPending(null);
    }
    setMessages((prev) => [...prev, { role: 'user', text }, { role: 'assistant', parts: [] }]);
    await run('/ai-assistant/chat', { conversation_id: activeId, message: text });
  };

  const answer = async (approve) => {
    if (!activeId || busy) return;
    setPending(null);
    await run(`/ai-assistant/conversations/${activeId}/confirm`, { approve });
  };

  return { activeId, messages, pending, busy, input, setInput, inputRef, send, answer, openConversation, newChat };
}
